import { getAuthTransportExpectation, reportSessionFailure } from '../auth/sessionSignals';

export type AppErrorKind =
  | 'session_expired'
  | 'access_denied'
  | 'network'
  | 'service_unavailable'
  | 'schema'
  | 'conflict'
  | 'validation'
  | 'unexpected';

type ErrorLike = {
  code?: unknown;
  status?: unknown;
  statusCode?: unknown;
  name?: unknown;
  message?: unknown;
  details?: unknown;
  constraint?: unknown;
};

type AppErrorOptions = {
  code?: string;
  status?: number;
  context?: string;
  cause?: unknown;
};

export type AppErrorSource = 'query' | 'mutation' | 'unknown';

const AUTH_ERROR_CODES = new Set([
  'PGRST301',
  'PGRST302',
  'PGRST303',
  'bad_jwt',
  'invalid_jwt',
  'invalid_refresh_token',
  'refresh_token_not_found',
  'session_not_found',
]);

const AUTH_ERROR_NAMES = new Set([
  'AuthSessionMissingError',
  'AuthInvalidJwtError',
  'AuthInvalidTokenResponseError',
]);

const SCHEMA_ERROR_CODES = new Set([
  '42P01',
  '42703',
  '42883',
  'PGRST202',
  'PGRST204',
  'PGRST205',
]);

const SERVICE_UNAVAILABLE_CODES = new Set([
  'PGRST000',
  'PGRST001',
  'PGRST002',
  'PGRST003',
  'PGRSTX00',
]);

const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

function errorLike(error: unknown): ErrorLike | null {
  return error && typeof error === 'object' ? error as ErrorLike : null;
}

function errorCode(error: unknown): string | undefined {
  if (typeof error === 'string' && /^(?:[0-9]{2}[A-Z0-9]{3}|PGRST\d+)$/i.test(error.trim())) return error.trim();
  const code = errorLike(error)?.code;
  return typeof code === 'string' && code.trim() ? code.trim() : undefined;
}

function errorStatus(error: unknown): number | undefined {
  const candidate = errorLike(error);
  const rawStatus = candidate?.status ?? candidate?.statusCode;
  const parsed = typeof rawStatus === 'number' ? rawStatus : Number(rawStatus);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function errorName(error: unknown): string | undefined {
  const name = errorLike(error)?.name;
  return typeof name === 'string' && name.trim() ? name.trim() : undefined;
}

function errorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  const message = errorLike(error)?.message;
  return typeof message === 'string' ? message : '';
}

function safeFallback(fallback: string | undefined): string {
  const trimmed = fallback?.trim();
  return trimmed || DEFAULT_FALLBACK;
}

function withRetryGuidance(fallback: string): string {
  return /\btry again\b/i.test(fallback) ? fallback : `${fallback} Please try again.`;
}

// Match known rules without displaying backend text, field values or identifiers.
function knownReason(error: unknown, source: AppErrorSource): string | undefined {
  const candidate = errorLike(error);
  const text = [errorMessage(error), candidate?.details, candidate?.constraint]
    .filter((part): part is string => typeof part === 'string').join(' ');
  if (/mobile_phone_verification_required|user_profiles_sms_requires_verified_mobile/.test(text)) {
    return 'Verify your mobile phone number before turning on text notifications.';
  }
  if (/mobile_phone_required/.test(text)) return 'Enter a mobile phone number first.';
  if (/mobile_phone_invalid/.test(text)) return 'Enter a valid mobile phone number, including its area code. Dots, dashes, spaces and parentheses are allowed.';
  if (/secondary_manager_requires_active_role/.test(text)) return 'The secondary manager must be an active manager or administrator. Choose another manager.';
  if (/manager_requires_active_role|user_profiles_(secondary_)?manager_role_chk/.test(text)) return 'The assigned manager must be an active manager or administrator. Ask an administrator to review the manager assignment.';
  if (/manager_cannot_be_self|user_profiles_(secondary_)?manager_not_self_chk/.test(text)) return 'A person cannot be their own manager. Choose another manager.';
  if (/manager_duplicate_assignment|user_profiles_secondary_manager_distinct_chk/.test(text)) return 'Choose different people for the primary and secondary managers.';
  if (/manager_cycle_detected/.test(text)) return 'This manager assignment would create a reporting loop. Choose another manager.';
  if (/user_profiles_home_site_chk/.test(text)) return 'Choose either a home site or a home region, rather than both.';
  if (/access preview/i.test(text)) return 'Changes cannot be saved while previewing another access role. Exit access preview and try again.';
  if (/protected_super_admin_profile_cannot_be_disabled/.test(text)) return 'This protected administrator account cannot be disabled.';
  if (/Your home site must be a site you are assigned to/.test(text)) return 'Choose a home site you are assigned to.';
  if (/You can only set a home site you cover/.test(text)) return 'Choose a home site you manage.';
  if (/duplicate key|unique constraint/i.test(text) || errorCode(error) === '23505') {
    if (/email/i.test(text)) return 'That email address is already in use. Enter another email address.';
    return 'A record with this information already exists. Use the existing record or enter a different value.';
  }
  if (/not-null constraint|null value in column/i.test(text) || errorCode(error) === '23502') {
    const column = /(?:column|field) ["']([^"']+)["']/i.exec(text)?.[1];
    const label = ({ first_name: 'First name', last_name: 'Last name', full_name: 'Display name', email: 'Email address', name: 'Name', phone: 'Phone number', mobile_phone: 'Mobile phone number' } as Record<string, string>)[column ?? ''];
    return label ? `${label} is required. Enter it before saving.` : 'A required field is missing. Fill in the required fields before saving.';
  }
  if (errorCode(error) === '23503' || /foreign key constraint/i.test(text)) return 'A linked record is no longer available. Refresh and choose an available record before saving.';
  if (errorCode(error) === '23514' || /check constraint/i.test(text)) return 'One of the values is not allowed. Review the fields and correct it before saving.';
  if (errorCode(error) === '40001' || errorCode(error) === '40P01') return source === 'mutation'
    ? 'Someone else changed this information while you were editing. Refresh and review their changes before saving again.'
    : 'This information changed while loading. Try refreshing it again.';
  if (errorCode(error) === '57014' || /statement timeout|request timed out|timeout exceeded/i.test(text)) {
    if (source === 'mutation') return 'The save took too long to complete. Check whether your changes saved before trying again.';
    if (source === 'query') return 'This information took too long to load. Try refreshing it again.';
    return 'The operation took too long to complete. Check its result before trying again.';
  }
  if (errorName(error) === 'AbortError' || /^AbortError:|^The operation was aborted\.?$/i.test(text)) {
    return source === 'mutation'
      ? 'Saving was interrupted. Check whether your changes saved before trying again.'
      : 'Loading was interrupted. Try refreshing this information again.';
  }
  if (errorCode(error) === 'PGRST116') return 'This record is no longer available to you. Refresh the page and check your access before saving again.';
  return undefined;
}

function classifyError(error: unknown): { kind: AppErrorKind; code?: string; status?: number } {
  const code = errorCode(error);
  const status = errorStatus(error);
  const name = errorName(error);
  const message = errorMessage(error);

  if (status === 401 || (code && AUTH_ERROR_CODES.has(code)) || (name && AUTH_ERROR_NAMES.has(name))) {
    return { kind: 'session_expired', code, status };
  }
  if (status === 403 || code === '42501') {
    return { kind: 'access_denied', code, status };
  }
  if (code && SCHEMA_ERROR_CODES.has(code)) {
    return { kind: 'schema', code, status };
  }
  if (code === '23505' || status === 409) {
    return { kind: 'conflict', code, status };
  }
  if (
    (code && SERVICE_UNAVAILABLE_CODES.has(code))
    || status === 408
    || status === 429
    || (typeof status === 'number' && status >= 500)
  ) {
    return { kind: 'service_unavailable', code, status };
  }
  if (
    name === 'AuthRetryableFetchError'
    || /failed to fetch|network(?:error| request)?|load failed|offline/i.test(message)
  ) {
    return { kind: 'network', code, status };
  }
  if (status === 400 || status === 422 || (code && (/^22/.test(code) || ['23502', '23503', '23514'].includes(code)))) {
    return { kind: 'validation', code, status };
  }
  return { kind: 'unexpected', code, status };
}

function messageFor(kind: AppErrorKind, fallback: string): string {
  switch (kind) {
    case 'session_expired':
      return 'Your session expired. Sign in again to continue.';
    case 'access_denied':
      return "Your account does not have permission to complete this action. Ask an administrator to review your access.";
    case 'network':
      return 'The app could not reach the service. Check your connection and try again.';
    case 'service_unavailable':
      return `${fallback} The service is temporarily unavailable. Please try again shortly.`;
    case 'schema':
      return `${fallback} The app and data service are out of sync. Refresh and try again. If it continues, contact support.`;
    case 'conflict':
      return `${fallback} This information changed. Refresh and try again.`;
    case 'validation':
      return `${fallback} Check required fields and number or date formats, then try again.`;
    case 'unexpected':
      return withRetryGuidance(fallback);
  }
}

/**
 * A UI-safe application error. The message is always curated by the app; raw
 * backend text is retained only as a non-enumerable cause for diagnostics.
 */
export class AppError extends Error {
  readonly kind: AppErrorKind;
  readonly code?: string;
  readonly status?: number;
  readonly context?: string;
  readonly cause?: unknown;

  constructor(kind: AppErrorKind, message: string, options: AppErrorOptions = {}) {
    super(message);
    this.name = 'AppError';
    this.kind = kind;
    this.code = options.code;
    this.status = options.status;
    this.context = options.context;
    Object.defineProperty(this, 'cause', {
      configurable: false,
      enumerable: false,
      value: options.cause,
      writable: false,
    });
  }
}

export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

export function isSessionExpiredError(error: unknown): error is AppError {
  return isAppError(error) && error.kind === 'session_expired';
}

export function toAppError(
  error: unknown,
  fallback: string = DEFAULT_FALLBACK,
  context?: string,
  source: AppErrorSource = 'query',
): AppError {
  if (isAppError(error)) return error;
  const rawClassification = classifyError(error);
  const classification = rawClassification.kind === 'session_expired'
    && getAuthTransportExpectation().mode === 'public'
    ? { ...rawClassification, kind: 'validation' as const }
    : rawClassification;
  const safeMessage = knownReason(error, source) ?? messageFor(classification.kind, safeFallback(fallback));
  const appError = new AppError(classification.kind, safeMessage, {
    ...classification,
    context,
    cause: error,
  });
  if (appError.kind === 'session_expired') {
    reportSessionFailure({
      reason: 'protected_request_unauthorized',
      source: source === 'unknown' ? 'query' : source,
      status: appError.status ?? 401,
    });
  }
  return appError;
}

/**
 * For a write that failed after an earlier step of the same action had saved. `message` says
 * what saved and what did not ("The purchase order was created, but …"). toAppError replaces the
 * message for permission and session errors, so for those the curated text is appended instead.
 */
export function toPartialSaveError(error: unknown, message: string, context?: string): AppError {
  const appError = toAppError(error, message, context, 'mutation');
  if (appError.kind !== 'access_denied' && appError.kind !== 'session_expired') return appError;
  if (appError.message.startsWith(message)) return appError;
  return new AppError(appError.kind, `${message} ${appError.message}`, {
    code: appError.code,
    status: appError.status,
    context: appError.context,
    cause: appError.cause,
  });
}

export type SupabaseQueryResult<T> = {
  data: T;
  error: unknown | null;
};

export function unwrapSupabaseResult<T>(
  result: SupabaseQueryResult<T>,
  fallback: string,
  context?: string,
  source: AppErrorSource = 'query',
): T {
  if (result.error) throw toAppError(result.error, fallback, context, source);
  return result.data;
}

export async function runSupabaseQuery<T>(
  loader: () => PromiseLike<SupabaseQueryResult<T>>,
  fallback: string,
  context?: string,
  source: AppErrorSource = 'query',
): Promise<T> {
  try {
    return unwrapSupabaseResult(await loader(), fallback, context, source);
  } catch (error) {
    throw toAppError(error, fallback, context, source);
  }
}

export function safeErrorMessage(error: unknown, fallback: string = DEFAULT_FALLBACK): string {
  return toAppError(error, fallback).message;
}

/** Also accepts existing UI copy, while replacing raw server messages and codes. */
export function readableErrorMessage(error: unknown, fallback = 'The app could not complete this action. Try again. If it still fails, contact support.', source: AppErrorSource = 'unknown'): string {
  if (isAppError(error)) return error.message;
  const reason = knownReason(error, source);
  if (reason) return reason;
  const message = errorMessage(error).trim();
  const classification = classifyError(error);
  if (classification.kind !== 'unexpected') return toAppError(error, fallback, undefined, source).message;
  const technical = /\b(?:PGRST\d+|SQLSTATE|[0-9]{2}[A-Z0-9]{3})\b|\b[a-z]+(?:_[a-z0-9]+)+\b|constraint|invalid input syntax|permission denied|row.level security|schema cache|\b(?:TypeError|ReferenceError|SyntaxError)\b|\b(?:SELECT .+ FROM|INSERT INTO|UPDATE .+ SET|DELETE FROM)\b|\b[0-9a-f]{8}-[0-9a-f-]{27,}\b|\bat .+\(.+:\d+|https?:\/\//i;
  if (message && !technical.test(message) && !/^(?:error|save error|autosave failed|\[object Object\]|undefined|null)$/i.test(message)) {
    if (/^(?:failed|unable|could not|couldn't) to (?:save|update|create)/i.test(message) && !/\bbecause\b/i.test(message)) return withRetryGuidance(message);
    return message;
  }
  return fallback;
}
